import { Button, Select, Spin } from 'antd';
import { useEffect, useState } from 'react';
import { Dynamics } from '@/services/dynamics/dynamics';
import { EEntity } from '@/services/dynamics/enums/entity.enum';
import { LoadingOutlined } from '@ant-design/icons';
import { DynRelatedToData } from '@/services/dynamics/interfaces/relate-to-info.interface';
import { showRelateToEntityText } from '@/shared/helpers/relate-to-name.helper';
import { INoteState } from '@/pages/Active/Active';
import { ISession } from '@/services/sip-js.service';
import { IEntity } from '@/shared/interfaces/entity.interface';
import { useStore } from '@/stores';
import '../Forms/Forms.scss';

const { Option } = Select;
const antIcon = <LoadingOutlined spin />;

interface IProps {
  assignToData: DynRelatedToData;
  onSelectAssignType: (value: EEntity) => void;
  assignEntityOptions: any;
  onItemSelected: (data: any) => void;
  onSearchAssignOptions?: (text: string) => void;
  client: any;
  savePhoneCallHandler: (
    note?: INoteState,
    session?: ISession,
    assignedEntity?: IEntity,
  ) => Promise<string>;
}

const errorText = 'This field is required';

const optionsRender = () => {
  const entities = [EEntity.CONTACT, EEntity.LEAD];

  return entities.map((e, i) => {
    return (
      <Option key={i} value={e}>
        {showRelateToEntityText(e)}
      </Option>
    );
  });
};

export const AssignBlock = ({
  assignToData,
  onSelectAssignType,
  assignEntityOptions,
  onItemSelected,
  onSearchAssignOptions,
  client,
  savePhoneCallHandler,
}: IProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    callStore: { setActiveContact, setCallNote, callNote },
  } = useStore();
  const [errors, setErrors] = useState({
    type: '',
    id: '',
  });

  const validate = () => {
    let isOk = true;
    const errors = {
      type: '',
      id: '',
    };
    if (!assignToData?.type) {
      errors.type = errorText;
      isOk = false;
    }
    if (!assignToData?.id) {
      errors.id = errorText;
      isOk = false;
    }

    setErrors(errors);
    return isOk;
  };

  useEffect(() => {
    setErrors({ type: '', id: '' });
  }, [assignToData?.id, assignToData?.type]);

  const findAssignEntity = async () => {
    let assignedEntity: IEntity | null = null;

    if (assignToData.type === EEntity.CONTACT) {
      const [contact] = await Dynamics.searchContacts({
        id: assignToData.id,
        isExactPhoneSearch: true,
      });
      assignedEntity = contact || null;
    }

    if (assignToData.type === EEntity.LEAD) {
      const [lead] = await Dynamics.searchLeads({
        id: assignToData.id,
        isExactPhoneSearch: true,
      });
      assignedEntity = lead || null;
    }

    return assignedEntity;
  };

  const sendData = async () => {
    const phone = client.phone ? client.phone.replace('Unknown', '') : '';
    const initialPhone = client.initialPhone
      ? client.initialPhone.replace('Unknown', '')
      : '';
    const secondPhone = client.secondPhone
      ? client.secondPhone.replace('Unknown', '')
      : '';
    const mobilePhone = phone || initialPhone || secondPhone;

    await Dynamics.updateRecord(assignToData.type, assignToData.id, {
      mobilephone: mobilePhone,
    });

    const assignedEntity: IEntity | null = await findAssignEntity();

    if (assignedEntity) {
      setActiveContact(assignedEntity);
    }

    setCallNote({ ...callNote, assignToData });

    await savePhoneCallHandler(null, null, assignedEntity);
    setIsLoading(false);
  };

  const submit = async () => {
    const canProceed = validate();
    if (!canProceed) return;
    setIsLoading(true);
    await sendData();
    await onSelectAssignType(null);
    await onItemSelected(null);
  };

  return isLoading ? (
    <Spin className="spinner" indicator={antIcon} />
  ) : (
    <div className="note__relate-to">
      <div className="main-text note__header">Assign to</div>
      <div className="note__relate-to-controls form-container">
        <Select
          className="note__select-entity"
          data-testid="select-assign-type"
          showSearch={false}
          placeholder="Select an entity"
          optionFilterProp="children"
          value={assignToData?.type || null}
          onChange={onSelectAssignType}
          filterOption={(input, option) =>
            (option?.children as unknown as string)
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          {optionsRender()}
        </Select>

        {errors.type ? (
          <div className="form-error" style={{ paddingLeft: '20px' }}>
            {errors.type}
          </div>
        ) : (
          <></>
        )}
        {assignToData?.type && (
          <>
            <Select
              showSearch
              labelInValue
              data-testid="select-assign-option"
              className="note__relate-options"
              options={assignEntityOptions}
              filterOption={false}
              onSearch={onSearchAssignOptions}
              value={
                assignEntityOptions?.find(
                  (item: any) => item.value === assignToData.id,
                ) || null
              }
              onSelect={onItemSelected}
              placeholder="Please choose an entity name"
            />

            {errors.id ? (
              <div className="form-error" style={{ paddingLeft: '20px' }}>
                {errors.id}
              </div>
            ) : (
              <></>
            )}
          </>
        )}
      </div>
      <div className="button-block" style={{ marginTop: '30px' }}>
        <Button onClick={submit} data-testid="submit-assign">
          Submit
        </Button>
      </div>
    </div>
  );
};
